import { ElementRef, Input, Directive, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import type { ComponentProps, ElementType } from 'react';
import type { Root } from 'react-dom/client';
import { createRoot } from 'react-dom/client';
import { Observable } from 'rxjs';
import { InjectorContextProviderService } from '../services/injector-context-provider.service';

@Directive({
  selector: '[reactComponent]'
})
export class ReactComponentDirective<Comp extends ElementType> implements OnInit, OnChanges, OnDestroy {
  @Input() reactComponent: Observable<Comp>;
  @Input() props: ComponentProps<Comp>;

  private root: Root = null;
  private importedReactComponent: Comp = null;

  constructor(private host: ElementRef, private injectorContextProviderService: InjectorContextProviderService) {}

  public ngOnInit(): void {
    this.reactComponent.subscribe((component) => {
      this.importedReactComponent = component;
      this.renderComponent();
    });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!changes.props) return;
    this.props = changes.props.currentValue as ComponentProps<Comp>;
    this.renderComponent();
  }

  private renderComponent(): void {
    if (!this.importedReactComponent) return;

    // singleton pattern
    if (!this.root) this.root = createRoot(this.host.nativeElement);
    if (!this.root) return;

    this.injectorContextProviderService.render(this.root, this.importedReactComponent, this.props);
  }

  public ngOnDestroy() {
    // wait one microtask to make sure that the react component is done rendering (logs errors otherwise)
    Promise.resolve().then(() => this.root?.unmount());
  }
}
